<template>
  <div class="admin-payments">
    <h2>{{ $t('legacy.Финансовый журнал') }}</h2>

    <div class="filters">
      <input
        v-model="filters.q"
        type="text"
        :placeholder="$t('legacy.Email или имя')"
        class="filter-input"
        @input="onFilterChange"
      />
      <select v-model="filters.status" class="filter-input filter-select" @change="onFilterChange">
        <option value="">{{ $t('legacy.Все статусы') }}</option>
        <option value="succeeded">{{ $t('legacy.Оплачен') }}</option>
        <option value="pending">{{ $t('legacy.Ожидает') }}</option>
        <option value="canceled">{{ $t('legacy.Отменён') }}</option>
      </select>
      <input v-model="filters.from" type="date" class="filter-input" @change="onFilterChange" />
      <input v-model="filters.to" type="date" class="filter-input" @change="onFilterChange" />
      <button class="btn-reset" @click="resetFilters">{{ $t('legacy.Сбросить') }}</button>
    </div>

    <div v-if="loading" class="loading">{{ $t('common.loading') }}</div>

    <div v-else-if="payments.length === 0" class="empty">
      {{ $t('legacy.Платежи не найдены') }}
    </div>

    <div v-else>
      <div class="summary">
        {{ $t('Всего записей') }}: <strong>{{ total }}</strong> &mdash;
        {{ $t('оплачено на текущей странице') }}:
        <span v-for="(sum, index) in succeededSums" :key="sum.currency">
          <strong>{{ sum.formatted }}</strong><span v-if="index < succeededSums.length - 1"> · </span>
        </span>
      </div>

      <div class="table-wrapper">
        <table class="payments-table">
          <thead>
            <tr>
              <th>{{ $t('legacy.Дата') }}</th>
              <th>{{ $t('legacy.Пользователь') }}</th>
              <th>{{ $t('legacy.Тариф') }}</th>
              <th>{{ $t('legacy.Сумма') }}</th>
              <th>{{ $t('legacy.Статус') }}</th>
              <th>{{ $t('legacy.paymentProvider') }}</th>
              <th>{{ $t('legacy.Способ оплаты') }}</th>
              <th>{{ $t('legacy.Автоплатёж') }}</th>
              <th>{{ $t('ID транзакции') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="p in payments" :key="p.id">
              <td class="date-cell">{{ formatDate(p.paid_at || p.created_at) }}</td>
              <td>
                <div class="user-cell">
                  <span class="user-email">{{ p.user_email || '—' }}</span>
                  <span v-if="p.user_name" class="user-name">{{ p.user_name }}</span>
                </div>
              </td>
              <td>
                <span class="plan-badge" :class="p.plan">{{ formatPlan(p.plan) }}</span>
              </td>
              <td class="amount">{{ formatAmount(p.amount, p.currency) }}</td>
              <td>
                <span class="status-badge" :class="p.status">{{ formatStatus(p.status) }}</span>
              </td>
              <td>{{ formatProvider(p.provider || p.payment_provider) }}</td>
              <td>
                <div class="method-cell" v-if="p.payment_method_type">
                  <span class="method-icon">{{ methodIcon(p.payment_method_type) }}</span>
                  <span class="method-label">{{ formatMethodType(p.payment_method_type) }}</span>
                </div>
                <span v-else class="na">N/A</span>
              </td>
              <td>
                <span v-if="p.payment_method_saved === true" class="saved-badge yes">{{ $t('legacy.Сохранён') }}</span>
                <span v-else-if="p.payment_method_saved === false" class="saved-badge no">{{ $t('legacy.Не сохранён') }}</span>
                <span v-else class="saved-badge na">N/A</span>
              </td>
              <td class="ext-id">{{ p.external_id || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="pagination">
        <button :disabled="page <= 1" @click="prevPage">← {{ $t('common.back') }}</button>
        <span>{{ $t('legacy.Страница') }} {{ page }} {{ $t('legacy.из') }} {{ totalPages }}</span>
        <button :disabled="page >= totalPages" @click="nextPage">{{ $t('legacy.Вперёд →') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import { adminApi } from '@/api/admin';

export default {
  name: 'AdminPayments',
  data() {
    return {
      payments: [],
      total: 0,
      page: 1,
      limit: 50,
      loading: false,
      filterTimer: null,
      filters: {
        q: '',
        from: '',
        to: '',
        status: ''
      }
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.limit));
    },
    succeededSums() {
      const totalsByCurrency = this.payments
        .filter(payment => payment.status === 'succeeded')
        .reduce((totals, payment) => {
          const currency = payment.currency || 'RUB';
          totals[currency] = (totals[currency] || 0) + Number(payment.amount || 0);
          return totals;
        }, {});

      return Object.entries(totalsByCurrency).map(([currency, total]) => ({
        currency,
        formatted: this.$formatCurrency(total, currency, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })
      }));
    }
  },
  mounted() {
    this.loadPayments();
  },
  methods: {
    async loadPayments() {
      this.loading = true;
      try {
        const params = { page: this.page };
        if (this.filters.q) params.q = this.filters.q;
        if (this.filters.from) params.from = this.filters.from;
        if (this.filters.to) params.to = this.filters.to;
        if (this.filters.status) params.status = this.filters.status;

        const response = await adminApi.getPayments(params);
        this.payments = response.data.payments;
        this.total = response.data.total;
      } catch (e) {
        console.error('Failed to load payments:', e);
      } finally {
        this.loading = false;
      }
    },
    onFilterChange() {
      clearTimeout(this.filterTimer);
      this.filterTimer = setTimeout(() => {
        this.page = 1;
        this.loadPayments();
      }, 400);
    },
    resetFilters() {
      this.filters = { q: '', from: '', to: '', status: '' };
      this.page = 1;
      this.loadPayments();
    },
    prevPage() {
      this.page--;
      this.loadPayments();
    },
    nextPage() {
      this.page++;
      this.loadPayments();
    },
    formatDate(dateStr) {
       if (!dateStr) return '—';
       return this.$formatDate(new Date(dateStr), {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    },
    formatPlan(plan) {
      const map = {
        monthly_base: 'Base', monthly_premium: 'Premium',
        base: 'Base', premium: 'Premium', paid: 'Premium'
      };
       if (plan && plan.startsWith('credits_')) return this.$t('legacy.Кредиты');
      return map[plan] || plan || '—';
    },
    formatStatus(status) {
       const map = {
         succeeded: this.$t('legacy.Оплачен'),
         pending: this.$t('legacy.Ожидает'),
         canceled: this.$t('legacy.Отменён')
       };
      return map[status] || status || '—';
    },
    formatProvider(provider) {
      const map = {
        yookassa: 'YooKassa',
        prodamus: 'Prodamus'
      };
      return map[provider] || provider || '—';
    },
    formatAmount(amount, currency) {
      if (!amount) return '—';
       return this.$formatCurrency(parseFloat(amount), currency || 'RUB', {
         minimumFractionDigits: 2,
         maximumFractionDigits: 2
       });
    },
    formatMethodType(type) {
      if (!type) return '—';
      const map = {
         bank_card: this.$t('legacy.Банковская карта'),
         sbp: this.$t('legacy.СБП'),
        sberbank: 'SberPay',
        tinkoff_bank: 'T-Pay',
        yoo_money: this.$t('legacy.ЮMoney'),
         cash: this.$t('legacy.Наличные')
      };
      return map[type] || type;
    },
    methodIcon(type) {
      const map = {
        bank_card: '💳',
        sbp: '⚡',
        sberbank: '🟢',
        tinkoff_bank: '🟡',
        yoo_money: '💜'
      };
      return map[type] || '💰';
    }
  }
};
</script>

<style scoped lang="scss">
.admin-payments {
  h2 {
    margin-bottom: 24px;
    font-size: 22px;
    color: #26374E;
  }
}

.filters {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  align-items: center;

  .filter-input {
    padding: 8px 12px;
    border: 1px solid #cce4f0;
    border-radius: 6px;
    font-size: 14px;
    color: #26374E;
    background: #fff;

    &:focus {
      outline: none;
      border-color: #496782;
    }
  }

  .filter-select {
    min-width: 140px;
  }

  .btn-reset {
    padding: 8px 16px;
    background: #e0f4fc;
    border: 1px solid #cce4f0;
    border-radius: 6px;
    color: #496782;
    cursor: pointer;
    font-size: 14px;

    &:hover {
      background: #c8eaf6;
    }
  }
}

.summary {
  margin-bottom: 16px;
  font-size: 14px;
  color: #496782;
}

.loading, .empty {
  text-align: center;
  padding: 40px;
  color: #888;
  font-size: 16px;
}

.table-wrapper {
  overflow-x: auto;
}

.payments-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);

  th, td {
    padding: 10px 14px;
    text-align: left;
    font-size: 13px;
    border-bottom: 1px solid #eef4f8;
    white-space: nowrap;
  }

  th {
    background: #e0f4fc;
    color: #26374E;
    font-weight: 600;
  }

  tr:last-child td {
    border-bottom: none;
  }

  tr:hover td {
    background: #f7fbfe;
  }
}

.date-cell {
  font-size: 12px;
  color: #666;
}

.user-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;

  .user-email {
    font-size: 13px;
    color: #26374E;
  }
  .user-name {
    font-size: 11px;
    color: #888;
  }
}

.plan-badge {
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;

  &.base, &.monthly_base {
    background: #e0f4fc;
    color: #496782;
  }
  &.premium, &.monthly_premium, &.paid {
    background: #26374E;
    color: #fff;
  }
}

.status-badge {
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;

  &.succeeded {
    background: #d4edda;
    color: #155724;
  }
  &.pending {
    background: #fff3cd;
    color: #856404;
  }
  &.canceled {
    background: #f8d7da;
    color: #721c24;
  }
}

.amount {
  font-weight: 600;
  color: #26374E;
}

.method-cell {
  display: flex;
  align-items: center;
  gap: 6px;

  .method-icon {
    font-size: 15px;
  }
  .method-label {
    font-size: 12px;
    color: #496782;
  }
}

.na {
  font-size: 12px;
  color: #bbb;
}

.saved-badge {
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;

  &.yes {
    background: #d4edda;
    color: #155724;
  }
  &.no {
    background: #f8d7da;
    color: #721c24;
  }
  &.na {
    background: #f0f0f0;
    color: #aaa;
  }
}

.ext-id {
  font-size: 11px;
  color: #aaa;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  margin-top: 20px;

  button {
    padding: 8px 16px;
    background: #e0f4fc;
    border: 1px solid #cce4f0;
    border-radius: 6px;
    color: #496782;
    cursor: pointer;
    font-size: 14px;

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
    &:not(:disabled):hover {
      background: #c8eaf6;
    }
  }

  span {
    font-size: 14px;
    color: #496782;
  }
}
</style>
